import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Heart, Plus, Sparkles, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useUserCredits } from '@/hooks/useUserCredits';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';
import type { Announcement } from '@/types/announcement';

interface CreditDisplayProps {
  onGetMore?: () => void;
  recentAnnouncements?: Announcement[];
}

const copy = {
  en: {
    heroHeadline: 'Moments Ready to Share',
    heroSubtext: 'A few honest words can mean more than you think.',
    createCTA: 'Create a Living Announcement',
    getMore: 'Get More Credits',
    noCredits: 'No credits remaining',
    getStarted: 'Get credits to start sharing',
    recentMoment: 'Recent',
    youHave: 'You have',
    opportunities: 'opportunities to let someone know they matter.',
    opportunity: 'opportunity to let someone know they matter.',
    writeOne: 'Write One Now',
  },
  es: {
    heroHeadline: 'Momentos Listos para Compartir',
    heroSubtext: 'Unas pocas palabras honestas pueden significar más de lo que piensas.',
    createCTA: 'Crear un Anuncio en Vida',
    getMore: 'Obtener Más Créditos',
    noCredits: 'Sin créditos restantes',
    getStarted: 'Obtén créditos para empezar',
    recentMoment: 'Reciente',
    youHave: 'Tienes',
    opportunities: 'oportunidades de hacerle saber a alguien que importa.',
    opportunity: 'oportunidad de hacerle saber a alguien que importa.',
    writeOne: 'Escribe Uno Ahora',
  },
  fr: {
    heroHeadline: 'Moments Prêts à Partager',
    heroSubtext: 'Quelques mots sincères peuvent signifier plus que vous ne pensez.',
    createCTA: 'Créer une Annonce Vivante',
    getMore: 'Obtenir Plus de Crédits',
    noCredits: 'Plus de crédits',
    getStarted: 'Obtenez des crédits pour commencer',
    recentMoment: 'Récent',
    youHave: 'Vous avez',
    opportunities: 'occasions de faire savoir à quelqu\'un qu\'il compte.',
    opportunity: 'occasion de faire savoir à quelqu\'un qu\'il compte.',
    writeOne: 'Écrire Maintenant',
  },
  pt: {
    heroHeadline: 'Momentos Prontos para Compartilhar',
    heroSubtext: 'Algumas palavras honestas podem significar mais do que você imagina.',
    createCTA: 'Criar um Anúncio Vivo',
    getMore: 'Obter Mais Créditos',
    noCredits: 'Sem créditos restantes',
    getStarted: 'Obtenha créditos para começar',
    recentMoment: 'Recente',
    youHave: 'Você tem',
    opportunities: 'oportunidades de mostrar a alguém que importa.',
    opportunity: 'oportunidade de mostrar a alguém que importa.',
    writeOne: 'Escreva Agora',
  },
  ar: {
    heroHeadline: 'لحظات جاهزة للمشاركة',
    heroSubtext: 'بضع كلمات صادقة يمكن أن تعني أكثر مما تتصور.',
    createCTA: 'إنشاء إعلان حي',
    getMore: 'احصل على المزيد من الأرصدة',
    noCredits: 'لا أرصدة متبقية',
    getStarted: 'احصل على أرصدة للبدء',
    recentMoment: 'الأخيرة',
    youHave: 'لديك',
    opportunities: 'فرص لإخبار شخص ما بأنه مهم.',
    opportunity: 'فرصة لإخبار شخص ما بأنه مهم.',
    writeOne: 'اكتب واحدة الآن',
  },
  ja: {
    heroHeadline: '共有する準備ができた瞬間',
    heroSubtext: '正直な言葉は、あなたが思う以上の意味を持ちます。',
    createCTA: 'リビングアナウンスメントを作成',
    getMore: 'クレジットをもっと',
    noCredits: 'クレジットがありません',
    getStarted: 'クレジットを取得して始めましょう',
    recentMoment: '最近',
    youHave: 'あなたには',
    opportunities: '回の機会があります。',
    opportunity: '回の機会があります。',
    writeOne: '今すぐ書く',
  },
  ko: {
    heroHeadline: '공유할 준비가 된 순간들',
    heroSubtext: '몇 마디 진실한 말이 생각보다 큰 의미를 가질 수 있습니다.',
    createCTA: '리빙 어나운스먼트 만들기',
    getMore: '크레딧 더 받기',
    noCredits: '크레딧이 없습니다',
    getStarted: '크레딧을 받아 시작하세요',
    recentMoment: '최근',
    youHave: '',
    opportunities: '번의 기회가 있습니다.',
    opportunity: '번의 기회가 있습니다.',
    writeOne: '지금 쓰기',
  },
  id: {
    heroHeadline: 'Momen Siap Dibagikan',
    heroSubtext: 'Beberapa kata jujur bisa berarti lebih dari yang Anda kira.',
    createCTA: 'Buat Living Announcement',
    getMore: 'Dapatkan Lebih Banyak Kredit',
    noCredits: 'Tidak ada kredit tersisa',
    getStarted: 'Dapatkan kredit untuk mulai berbagi',
    recentMoment: 'Terbaru',
    youHave: 'Anda memiliki',
    opportunities: 'kesempatan untuk memberi tahu seseorang bahwa mereka penting.',
    opportunity: 'kesempatan untuk memberi tahu seseorang bahwa mereka penting.',
    writeOne: 'Tulis Sekarang',
  },

  hi: {
    heroHeadline: 'शेयर करने के लिए तैयार पल',
    heroSubtext: 'कुछ ईमानदार शब्द आपकी सोच से कहीं अधिक मायने रख सकते हैं।',
    createCTA: 'एक Living Announcement बनाएं',
    getMore: 'और क्रेडिट प्राप्त करें',
    noCredits: 'कोई क्रेडिट शेष नहीं',
    getStarted: 'शेयर करना शुरू करने के लिए क्रेडिट प्राप्त करें',
    recentMoment: 'हाल का',
    youHave: 'आपके पास है',
    opportunities: 'किसी को यह बताने के अवसर कि वे मायने रखते हैं।',
    opportunity: 'किसी को यह बताने का अवसर कि वे मायने रखते हैं।',
    writeOne: 'अभी एक लिखें',
  },
};

export function CreditDisplay({ onGetMore, recentAnnouncements = [] }: CreditDisplayProps) {
  const { credits, isLoading, refetch } = useUserCredits();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const t = lc(copy, language);

  const recentPublished = useMemo(() => 
    recentAnnouncements
      .filter(a => a.status === 'approved')
      .slice(0, 2),
    [recentAnnouncements]
  );

  const handleGetMore = () => {
    navigate('/add-credits');
  };

  if (isLoading) {
    return (
      <Card className="glass border-0 shadow-soft rounded-2xl overflow-hidden">
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-5 bg-muted rounded w-2/3" />
            <div className="h-4 bg-muted rounded w-full" />
            <div className="h-10 bg-muted rounded w-1/2" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!credits) {
    return (
      <Card className="glass border-0 shadow-soft rounded-2xl overflow-hidden">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-destructive/10">
              <Heart className="h-5 w-5 text-destructive" />
            </div>
            <div>
              <p className="font-semibold text-foreground">Couldn't load account data</p>
              <p className="text-sm text-muted-foreground">This can happen on slow connections. Please try again.</p>
            </div>
          </div>
          <Button onClick={() => refetch()} variant="outline" className="w-full gap-2 rounded-xl">
            Try again
          </Button>
        </CardContent>
      </Card>
    );
  }

  const creditsRemaining = credits.announcement_credits;
  const isEmpty = creditsRemaining === 0;

  if (isEmpty) {
    return (
      <Card className="glass border-0 shadow-soft rounded-2xl overflow-hidden">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-muted">
              <Heart className="h-5 w-5 text-muted-foreground" />
            </div>
            <div>
              <p className="font-semibold text-foreground">{t.noCredits}</p>
              <p className="text-sm text-muted-foreground">{t.getStarted}</p>
            </div>
          </div>
          
          <Button onClick={handleGetMore} className="w-full gap-2 rounded-xl">
            <Sparkles className="h-4 w-4" />
            {t.getMore}
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="glass border-0 shadow-soft rounded-2xl overflow-hidden">
      {/* Gradient accent strip */}
      <div className="h-1 bg-gradient-to-r from-primary via-primary/70 to-accent/50" />

      <CardContent className="p-6 space-y-5">
        {/* Hero headline */}
        <div className="space-y-1">
          <h2 className="text-xl font-bold text-foreground">{t.heroHeadline}</h2>
          <p className="text-muted-foreground text-sm">{t.heroSubtext}</p>
        </div>

        {/* Credits counter */}
        <motion.div
          className="flex items-center gap-4 p-4 rounded-xl bg-primary/5 border border-primary/10"
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.2, type: 'spring' }}
        >
          <div className="p-2.5 rounded-xl bg-primary/10">
            <Heart className="h-5 w-5 text-primary" fill="currentColor" />
          </div>
          <div className="flex-1">
            <p className="text-sm text-foreground">
              {t.youHave}{' '}
              <span className="text-2xl font-bold text-primary">{creditsRemaining}</span>{' '}
              {creditsRemaining === 1 ? t.opportunity : t.opportunities}
            </p>
          </div>
        </motion.div>

        {/* Primary CTA */}
        <Link to="/create" className="block">
          <Button size="lg" className="w-full gap-2 rounded-xl group">
            <Plus className="h-5 w-5 group-hover:rotate-90 transition-transform duration-300" />
            {t.writeOne}
          </Button>
        </Link>

        {/* Recent announcements */}
        {recentPublished.length > 0 && (
          <div className="pt-4 border-t border-border/30 space-y-2">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              {t.recentMoment}
            </p>
            {recentPublished.map((announcement) => (
              <Link 
                key={announcement.id}
                to={`/a/${announcement.public_slug}`}
                className="block group"
              >
                <div className="flex items-center justify-between py-2 px-3 -mx-1 rounded-lg hover:bg-muted/50 transition-colors">
                  <span className="text-sm truncate flex-1 mr-2 text-foreground group-hover:text-primary transition-colors">
                    {announcement.recipient_name}
                  </span>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-muted-foreground shrink-0">
                      {new Date(announcement.published_at || announcement.created_at).toLocaleDateString(undefined, {
                        month: 'short',
                        day: 'numeric',
                      })}
                    </span>
                    <ArrowRight className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
